// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as apiLogin,
  authenticatedRequest,
  refreshAccessToken,
  logout as apiLogout,
  setAccessToken,
} from '../api/auth';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        if (typeof refreshAccessToken === 'function') {
          const token = await refreshAccessToken();
          if (token) {
            setIsAuthenticated(true);
            return;
          }
        }

        const stored = localStorage.getItem('token');
        if (stored) {
          try {
            await authenticatedRequest('/transactions', 'GET', null, stored);
            setIsAuthenticated(true);
          } catch (_) {
            localStorage.removeItem('token');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Session validation failed:', error);
        setIsAuthenticated(false);
      }
    };

    checkSession();
  }, []);

  const login = async (arg1, arg2) => {
    try {
      // If second argument is an object we assume token + user were provided
      if (arg2 && typeof arg2 === 'object') {
        localStorage.setItem('token', arg1);
        setAccessToken(arg1);
        setIsAuthenticated(true);
        navigate('/dashboard');
        return;
      }

      const result = await apiLogin(arg1, arg2);

      if (!result.success) {
        throw new Error(result.error || 'Invalid credentials');
      }

      const { accessToken } = result;
      localStorage.setItem('token', accessToken);
      setAccessToken(accessToken);
      setIsAuthenticated(true);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Invalid credentials');
    }
  };

  const logout = async () => {
    await apiLogout();
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
